import { ReactElement, MouseEvent } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useState } from 'react';
import { Tune } from '@mui/icons-material';
import {
  CUSTOM_VALUE_LABEL,
  NATURAL_DELIVERY_LABEL,
  ZERO_PERCENT_PEAK_LABEL
} from './constants';
import COLORS from '../../colors';
import PeakIcon from './PeakIcon';
import NaturalDeliveryIcon from './NaturalDeliveryIcon';
import PeakPercentageChip from './PeakPercentageChip';

interface PeakPercentageLabelsProps {
  maximumPeakPercentage: number;
  onLabelSelect: (alignment: string | null) => void;
}

const PeakPercentageLabels = ({
  maximumPeakPercentage,
  onLabelSelect
}: PeakPercentageLabelsProps) => {
  const [alignment, setAlignment] = useState<string | null>('left');

  const handleAlignment = (
    _: MouseEvent<HTMLElement>,
    newAlignment: string | null
  ): void => {
    if (userClickedSameLabel(newAlignment)) {
      return;
    }

    setAlignment(newAlignment);
    onLabelSelect(newAlignment);
  };

  const userClickedSameLabel = (newAlignment: string | null): boolean => {
    return !newAlignment || newAlignment === alignment;
  };

  const renderChip = (
    value: string,
    label: string,
    icon: ReactElement,
    isSelected: boolean
  ) => {
    return (
      <ToggleButton
        value={value}
        aria-label={`${value} aligned`}
        sx={{ margin: 0, padding: 0, borderWidth: 0 }}
      >
        <PeakPercentageChip
          isSelected={isSelected}
          label={label}
          icon={isSelected ? undefined : icon}
        />
      </ToggleButton>
    );
  };

  return (
    <ToggleButtonGroup
      value={alignment}
      exclusive
      onChange={handleAlignment}
      aria-label="peak-percentage"
      sx={{ gap: '12px' }}
    >
      {renderChip(
        CUSTOM_VALUE_LABEL,
        'Choose Custom Value',
        <Tune sx={{ fill: COLORS.NEUTRAL_200, width: '18px' }} />,
        alignment === CUSTOM_VALUE_LABEL
      )}
      {renderChip(
        ZERO_PERCENT_PEAK_LABEL,
        'Use 0% Peak',
        <PeakIcon />,
        alignment === ZERO_PERCENT_PEAK_LABEL
      )}
      {renderChip(
        NATURAL_DELIVERY_LABEL,
        'Max',
        <NaturalDeliveryIcon peakPercentage={maximumPeakPercentage} />,
        alignment === NATURAL_DELIVERY_LABEL
      )}
    </ToggleButtonGroup>
  );
};

export default PeakPercentageLabels;
